"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, ShieldCheck, Clock, Camera, CircleCheck as CheckCircle2, ArrowRight, TriangleAlert as AlertTriangle } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const highlights = [{
  title: "Video Camera Inspection",
  desc: "Pinpoint tree roots, breaks, and collapses with HD diagnostic cameras.",
  icon: Camera
}, {
  title: "24/7 Rapid Response",
  desc: "Fast emergency dispatch across Jonesboro and Clayton County.",
  icon: Clock
}, {
  title: "Licensed & Guaranteed",
  desc: "Complete repair and trenchless solutions backed by warranty.",
  icon: ShieldCheck
}];
const quickSigns = ["Multiple backing up drains", "Foul sewer odor indoors or in yard", "Gurgling toilets & slow drains", "Soggy or lush patches on your lawn"];
export default function SewerHero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1,
        delayChildren: shouldReduceMotion ? 0 : 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="sewerlinerepairhero" data-nav="light" className="relative overflow-hidden bg-background py-12 md:py-20 lg:py-24 border-b border-border">
      {/* Background Subtle Highlights */}
      <div className="pointer-events-none absolute inset-0 -z-10 flex items-center justify-center overflow-hidden">
        <div className="absolute -top-32 -right-32 size-96 rounded-full bg-accent/10 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 size-96 rounded-full bg-primary/10 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
          {/* Main Hero Content */}
          <div className="lg:col-span-7 space-y-6">
            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 font-medium text-xs sm:text-sm">
                Jonesboro Emergency Plumber
              </Badge>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/15 px-3 py-1 text-xs font-semibold text-accent-foreground border border-accent/30">
                <span className="size-2 rounded-full bg-accent animate-pulse" />
                24/7 Immediate Dispatch
              </span>
            </motion.div>

            <motion.h1 variants={itemVariants} className="font-default text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.12]">
              Emergency Sewer Line Repair in Jonesboro, GA
            </motion.h1>

            <motion.p variants={itemVariants} className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              Clogged main lines, broken sewer pipes, or raw sewage backup? We provide 
              immediate video pipe inspections and durable repair solutions to restore your 
              home safely without delay.
            </motion.p>

            {/* Quick Diagnostic Warning signs */}
            <motion.div variants={itemVariants} className="rounded-lg border border-border bg-card p-4 sm:p-5">
              <div className="flex items-center gap-2 text-sm font-semibold text-foreground mb-3">
                <AlertTriangle className="size-4 text-accent" />
                <span>Common warning signs of a broken or blocked sewer main:</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs sm:text-sm text-card-foreground">
                {quickSigns.map((sign, i) => <div key={i} data-index={i} className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 shrink-0 text-primary" />
                    <span>{sign}</span>
                  </div>)}
              </div>
            </motion.div>

            {/* CTAs */}
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-6 py-3 h-auto text-base shadow-md">
                <a href="tel:4708479076" className="inline-flex items-center justify-center gap-2">
                  <PhoneCall className="size-5" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>

              <Button asChild variant="outline" size="lg" className="font-semibold px-6 py-3 h-auto text-base">
                <Link to="/contact/" className="inline-flex items-center justify-center gap-2">
                  <span>Request Inspection</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </motion.div>

            {/* Micro reassurance label */}
            <motion.p variants={itemVariants} className="text-xs text-muted-foreground">
              {"Average emergency technician arrival: under 60 minutes in Jonesboro area."}
            </motion.p>
          </div>

          {/* Right Visual & Feature Card */}
          <motion.div variants={itemVariants} className="lg:col-span-5 space-y-4">
            <Card className="border-border shadow-lg overflow-hidden bg-card">
              <div className="relative aspect-4/3 w-full overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/sewer_line_camera_inspection.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/sewer_line_camera_inspection.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/sewer_line_camera_inspection.png-wvc-sizes" alt="Plumber operating video camera inspection equipment for sewer line diagnosis" loading="lazy" decoding="async" className="size-full object-cover" />
                <div className="absolute top-3 left-3 bg-background/90 backdrop-blur-xs text-foreground px-3 py-1 rounded-md text-xs font-semibold shadow-xs flex items-center gap-1.5 border border-border">
                  <Camera className="size-3.5 text-primary" />
                  <span>HD Pipe Camera Diagnostic</span>
                </div>
              </div>

              <CardContent className="p-6 space-y-4">
                <h2 className="font-default text-lg font-bold text-card-foreground">
                  Why Call Jonesboro Emergency Plumber?
                </h2>

                <div className="space-y-3">
                  {highlights.map((item, i) => {
                  const IconComponent = item.icon;
                  return <div key={item.title} data-index={i} className="flex items-start gap-3 rounded-md p-2 transition-colors hover:bg-muted/50">
                        <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-secondary text-secondary-foreground">
                          <IconComponent className="size-4" />
                        </div>
                        <div className="text-left">
                          <h3 className="font-default text-sm font-semibold text-card-foreground">
                            {item.title}
                          </h3>
                          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                            {item.desc}
                          </p>
                        </div>
                      </div>;
                })}
                </div>

                <div className="pt-2 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                  <span>Upfront estimates</span>
                  <span>No hidden overtime fees</span>
                  <span>Local crew</span>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}